import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentMethodRequiredError } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { useToast } from '@/shared/ui/toast';
import { useOptionalPaywallContext } from '../model/PaywallContext';
import { isConversionTransition } from '../model/isConversionTransition';

export const useWorkflowTransitionWithPaywall = () => {
  const ctx = useOptionalPaywallContext();
  const { sessionUser } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  const runTransition = useCallback(
    async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
      const preSub = sessionUser?.subscription;
      try {
        const result = await fn();
        if (
          preSub &&
          preSub.tier === 'free' &&
          preSub.paymentMethodOnFile &&
          preSub.freeRosRemaining === 0 &&
          isConversionTransition(result)
        ) {
          toast({ title: t('subscription.paywall.postConversionToast') });
        }
        return result;
      } catch (err) {
        if (err instanceof PaymentMethodRequiredError && ctx) {
          ctx.openPaywall({ pricePerRo: err.pricePerRo, allowance: err.allowance, used: err.used });
          return undefined;
        }
        throw err;
      }
    },
    [sessionUser, ctx, toast, t]
  );

  return {
    runTransition,
    paywallState: ctx?.paywallState ?? null,
    clearPaywall: ctx?.closePaywall ?? (() => undefined),
  };
};
